import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  HandHeart, BookOpen, Sprout, Stethoscope, Users, Globe, 
  Heart, Briefcase, Home, ArrowRight, CheckCircle 
} from 'lucide-react';
import { Button } from '../components/ui/button';

const PAYPAL_LINK = "https://www.paypal.com/donate/?hosted_button_id=ZUX3BKRN524VS";

const programs = [
  {
    id: 'poverty',
    icon: HandHeart,
    title: 'Poverty Alleviation',
    description: 'Economic empowerment through sustainable livelihoods, microfinance, and skills training.',
    color: '#E08B7A',
    image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '15,000+ families supported',
    initiatives: ['Microfinance programs', 'Vocational training', 'Small business support', 'Financial literacy']
  },
  {
    id: 'education',
    icon: BookOpen,
    title: 'Education & Skills Development',
    description: 'Building futures through quality education, scholarships, and skills training programs.',
    color: '#7BC4E2',
    image: 'https://images.unsplash.com/photo-1574465636377-7781c5117a0c?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '10,000+ students reached',
    initiatives: ['School construction', 'Teacher training', 'Scholarships', 'Digital literacy']
  },
  {
    id: 'climate',
    icon: Sprout,
    title: 'Climate Action & Environment',
    description: 'Environmental sustainability and climate resilience for vulnerable communities.',
    color: '#9DB9A3',
    image: 'https://images.unsplash.com/photo-1761634373241-5abcfcaa0c39?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '100,000+ trees planted',
    initiatives: ['Reforestation', 'Clean energy', 'Climate adaptation', 'Sustainable agriculture']
  },
  {
    id: 'health',
    icon: Stethoscope,
    title: 'Health & Well-being',
    description: 'Community health programs, disease prevention, and healthcare access.',
    color: '#E5B75F',
    image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '25,000+ people served',
    initiatives: ['Health clinics', 'Vaccination drives', 'Maternal care', 'Mental health support']
  },
  {
    id: 'youth',
    icon: Users,
    title: 'Youth Empowerment',
    description: 'Enabling youth for employment, leadership, and active citizenship.',
    color: '#7BC4E2',
    image: 'https://images.unsplash.com/photo-1529390079861-591de354faf5?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '5,000+ youth trained',
    initiatives: ['Leadership programs', 'Job placement', 'Entrepreneurship', 'Mentorship']
  },
  {
    id: 'gender',
    icon: Heart,
    title: 'Gender Equality',
    description: 'Women empowerment, gender-based violence prevention, and equal opportunities.',
    color: '#E08B7A',
    image: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '8,000+ women empowered',
    initiatives: ['Women cooperatives', 'GBV prevention', 'Leadership training', 'Economic inclusion']
  },
  {
    id: 'humanitarian',
    icon: Globe,
    title: 'Humanitarian Response',
    description: 'Rapid response to emergencies, disasters, and crises worldwide.',
    color: '#9DB9A3',
    image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '30+ emergency responses',
    initiatives: ['Emergency relief', 'Refugee support', 'Disaster recovery', 'Food security']
  },
  {
    id: 'agriculture',
    icon: Sprout,
    title: 'Agriculture & Rural Development',
    description: 'Sustainable farming, food security, and rural community development.',
    color: '#E5B75F',
    image: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '12,000+ farmers supported',
    initiatives: ['Farming training', 'Irrigation systems', 'Market access', 'Seed distribution']
  },
  {
    id: 'international',
    icon: Globe,
    title: 'International Cooperation & Development',
    description: 'Building global partnerships for sustainable development and cross-border collaboration.',
    color: '#7BC4E2',
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85',
    impact: '20+ international partnerships',
    initiatives: ['UN collaboration', 'Policy advocacy', 'Knowledge exchange', 'Capacity building']
  }
];

const impactStories = [
  {
    title: 'Sarah\'s Education Journey',
    location: 'Kenya',
    story: 'Through OPAD\'s scholarship program, Sarah became the first in her village to attend university.',
    image: 'https://images.unsplash.com/photo-1544717305-2782549b5136?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Clean Water for All',
    location: 'Uganda',
    story: 'Our water project brought clean drinking water to 5,000 people in rural Uganda.',
    image: 'https://images.unsplash.com/photo-1541544537156-7627a7a4aa1c?crop=entropy&cs=srgb&fm=jpg&q=85'
  },
  {
    title: 'Women Entrepreneurs',
    location: 'Ghana',
    story: 'A women\'s cooperative now supports 200 families through sustainable businesses.',
    image: 'https://images.unsplash.com/photo-1573497019418-b400bb3ab074?crop=entropy&cs=srgb&fm=jpg&q=85'
  }
];

export default function ProgramsPage() {
  return (
    <div data-testid="programs-page">
      {/* Hero Section */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Our Programmes
              </h1>
              <p className="text-lg text-slate-600">
                We work across multiple sectors to create lasting impact and sustainable change in communities worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-16">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                id={program.id}
                className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                data-testid={`program-section-${program.id}`}
              >
                <div className={index % 2 !== 0 ? 'md:order-2' : ''}>
                  <div 
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                    style={{ backgroundColor: `${program.color}20` }}
                  >
                    <program.icon className="w-7 h-7" style={{ color: program.color }} />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-4">
                    {program.title}
                  </h2>
                  <p className="text-slate-600 mb-6 text-lg">{program.description}</p>
                  
                  <div className="bg-slate-50 rounded-xl p-4 mb-6">
                    <p className="text-sm text-slate-500 mb-1">Impact</p>
                    <p className="text-xl font-bold" style={{ color: program.color }}>{program.impact}</p>
                  </div>

                  <h4 className="font-semibold text-slate-900 mb-3">Key Initiatives:</h4>
                  <ul className="space-y-2 mb-6">
                    {program.initiatives.map((initiative, i) => (
                      <li key={i} className="flex items-center gap-2 text-slate-600">
                        <CheckCircle className="w-4 h-4" style={{ color: program.color }} />
                        {initiative}
                      </li>
                    ))}
                  </ul>

                  <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer">
                    <Button 
                      className="rounded-full px-6"
                      style={{ backgroundColor: program.color, color: program.color === '#E5B75F' ? '#0F172A' : '#fff' }}
                    >
                      Support This Program
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                </div>
                
                <div className={index % 2 !== 0 ? 'md:order-1' : ''}>
                  <img
                    src={program.image}
                    alt={program.title}
                    className="rounded-2xl shadow-lg w-full h-[350px] object-cover"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Programme Stories */}
      <section className="section-padding bg-white border-t border-slate-100" data-testid="featured-stories">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#E5B75F]/15 text-[#A47A1F] px-3 py-1 rounded-full text-xs font-semibold mb-3">In Action</span>
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Programmes In Action
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Real impact from our youth empowerment and humanitarian response programmes worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Youth Empowerment Story */}
            <motion.div
              className="bg-[#FDF8F4] rounded-3xl overflow-hidden shadow-md border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="story-un-youth"
            >
              <div className="p-8">
                <span className="inline-block bg-[#7BC4E2]/15 text-[#3A8FB5] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                  Youth Empowerment
                </span>
                <h3 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900 mb-3">
                  OPAD in Action: Empowering Youth Through United Nations Engagement
                </h3>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                  OPAD provided three students — Ying Xu, Quiayi Fan, and Chenyu Dong — with temporary access to the United Nations, gaining firsthand exposure to high-level diplomatic and multilateral processes over a three-day visit.
                </p>
                <h4 className="font-semibold text-slate-900 text-sm mb-2">Sessions Engaged</h4>
                <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5 mb-4">
                  <li>Commission on the Status of Women</li>
                  <li>Peacebuilding Commission discussions</li>
                  <li>SDG Dialogues — focus on SDG 3: Good Health and Well-being</li>
                  <li>Commemoration of World Water Day</li>
                </ul>
                <p className="text-sm text-slate-600 italic">
                  From observation to action — students developed SDG-aligned project proposals and translated insights into local action within their schools and universities.
                </p>
                <p className="mt-4 text-sm font-semibold text-[#4A7C59]">
                  "OPAD — Hope in Action. Together we change lives."
                </p>
              </div>
            </motion.div>

            {/* Humanitarian Food Distribution Story */}
            <motion.div
              className="bg-[#FDF8F4] rounded-3xl overflow-hidden shadow-md border border-slate-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="story-food-distribution"
            >
              <div className="grid grid-cols-2 gap-1">
                <img
                  src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/id1kvypv_food%2003.jpeg"
                  alt="OPAD food distribution Botkyrka"
                  className="w-full h-48 object-cover"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_10eec864-0b4d-4948-b760-389570b9159b/artifacts/hq6n4kfv_food%2005.jpeg"
                  alt="OPAD food distribution Botkyrka"
                  className="w-full h-48 object-cover"
                />
              </div>
              <div className="p-8">
                <span className="inline-block bg-[#9DB9A3]/20 text-[#3D6249] px-3 py-1 rounded-full text-xs font-semibold mb-3">
                  Humanitarian Response
                </span>
                <h3 className="text-xl md:text-2xl font-bold font-['Manrope'] text-slate-900 mb-3">
                  Food Distribution in Botkyrka Municipality, Sweden
                </h3>
                <p className="text-slate-600 mb-4 text-sm leading-relaxed">
                  OPAD implements a community-based food distribution programme in Botkyrka — operating in <strong>Alby, Fittja, and Hallunda-Norsborg</strong> — supporting low-income households, migrants, and socially excluded groups. The initiative reduces food insecurity, promotes social inclusion, and strengthens community solidarity.
                </p>
                <h4 className="font-semibold text-slate-900 text-sm mb-2">Key Activities</h4>
                <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
                  <li>Weekly food distribution to vulnerable households</li>
                  <li>Collaboration with local partners to reduce food waste</li>
                  <li>Community outreach and engagement</li>
                  <li>Support for newly arrived migrants and families in need</li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Stories */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Impact Stories
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Real stories of transformation from communities we serve.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {impactStories.map((story, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-sm card-lift"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`impact-story-${index}`}
              >
                <img
                  src={story.image}
                  alt={story.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <span className="text-xs font-medium text-[#7BC4E2] bg-[#7BC4E2]/10 px-2 py-1 rounded-full">
                    {story.location}
                  </span>
                  <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mt-3 mb-2">
                    {story.title}
                  </h3>
                  <p className="text-slate-600">{story.story}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#7BC4E2] section-padding">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Join Us in Making a Difference
            </h2>
            <p className="text-xl text-slate-800 max-w-2xl mx-auto mb-8">
              Your support enables us to continue our vital work in communities worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={PAYPAL_LINK} target="_blank" rel="noopener noreferrer" data-testid="programs-donate-button">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-10 py-6 text-lg font-semibold">
                  <Heart className="w-5 h-5 mr-2" />
                  Donate Now
                </Button>
              </a>
              <Link to="/get-involved" data-testid="programs-volunteer-button">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-10 py-6 text-lg font-semibold">
                  Volunteer With Us
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
